import { useMemo, useReducer } from "react";
import { useTranslation } from "react-i18next";
import { Games } from "../../Games";
import { FillGame, FillGameAction } from "./FillGame";
import { FillGameDraggable } from "./components.tsx/FillGameDraggable";
import { FillGameLink } from "./components.tsx/FillGameLink";

export const useFillGameReducer = ({
  current,
}: {
  current: Games | undefined;
}) => {
  //  CONTEXTS
  const { t } = useTranslation();

  //  STATES
  const [data, dispatch] = useReducer(
    (data: FillGame, action: FillGameAction): FillGame => {
      switch (action.type) {
        case "dropTo":
          return {
            ...data,
            droppables: data.droppables?.map((drop) => {
              if (drop.id === action.to) return { ...drop, have: action.drag };
              else return drop;
            }),
          };
        case "switch":
          return {
            ...data,
            droppables: data.droppables?.map((drop) => {
              if (drop.id === action.to) return { ...drop, have: action.drag };
              else if (drop.id === action.from)
                return {
                  ...drop,
                  have: data.droppables?.find((drop) => drop.id === action.to)
                    ?.have,
                };
              else return drop;
            }),
          };
        case "reset":
          return {
            ...data,
            droppables: data.droppables?.map((drop) => {
              if (drop.have === action.id) return { ...drop, have: "" };
              else return drop;
            }),
          };
        case "validate":
          const validItems = data.droppables?.reduce(
            (acc, drop) => {
              if (!drop.want?.includes(drop.have ?? "")) acc.isValid = false;
              else acc.validCount += 1;
              return acc;
            },
            { isValid: true, validCount: 0 }
          );
          return {
            ...data,
            isValid: validItems?.isValid ?? false,
            progress:
              (validItems?.validCount ?? 1) / (data.draggables?.length ?? 1),
          };
        default:
          return { ...data };
      }
    },
    {
      ...current,
      isValid: false,
      progress: 0,
      draggables: (current as FillGame).draggables.sort(
        () => Math.random() - 0.5
      ),
      droppables: (current as FillGame).links.reduce((acc, link) => {
        return [...acc, ...link.filter((block) => block.type === "drop")];
      }, []),
    } as FillGame
  );

  //  DATAS
  const draggables = useMemo(() => {
    console.log("rerender draggables");
    return data.draggables
      .filter((drag) => {
        return !data.droppables?.find((drop) => {
          return drop.have === drag.id;
        });
      })
      .map((drag, i) => (
        <FillGameDraggable
          key={drag.id}
          id={drag.id}
          hostedBy=""
          disabled={data.isValid ?? false}
        >
          {t(`games.${current?.id}.${drag.id}`)}
        </FillGameDraggable>
      ));
  }, [t, current, data.droppables, data.draggables, data.isValid]);

  const links = useMemo(() => {
    return data.links.map((link, i) => {
      return (
        <FillGameLink
          key={`link${i}`}
          mapKey={i}
          data={data}
          link={link}
          isValid={data.isValid ?? false}
        />
      );
    });
  }, [data]);

  return { dispatch, draggables, links, data };
};
